import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, catchError, combineLatest, distinctUntilChanged, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { LiveReconnectHintComponent } from '../../../components/live-reconnect-hint/live-reconnect-hint.component';
import { LivePipeCardComponent } from './live-pipe-card.component';
import { LivePipelineService } from '../../../services/live-pipeline.service';
import {
  LivePipeline,
  LivePipelineEntry,
  livePipelineKey,
  runningLivePipelines,
} from '../../../models/live-pipeline.model';
import { ProjectService } from '../../../services/project.service';
import { RepositoryBindingService } from '../../../services/repository-binding.service';
import { sameIds } from '../../models/project-selection';

/**
 * Die Dashboard-Sektion „Pipelines · Live" (NEX-1232): je laufendem Durchlauf aus einem
 * angebundenen Repository eine Karte.
 *
 * <p>Gefragt wird je Projekt, weil der Endpunkt je Projekt antwortet; die Liste der
 * Repository-Bindungen entscheidet vorher, welche Projekte ueberhaupt gefragt
 * werden. Ohne eine einzige Bindung bleibt die Sektion fort — sie waere sonst eine
 * dauerhaft leere Kachel fuer eine Integration, die niemand eingerichtet hat.
 *
 * <p>Nachgeladen wird nur nach einem Reconnect: neue Staende und neu gestartete
 * Durchlaeufe kommen ueber das Projekt-Topic herein, und der Eintrag darin ist
 * derselbe, den auch der Endpunkt liefert. Was das Topic waehrend der Luecke
 * gemeldet haette, ist aber verloren — ohne einmaligen Abgleich per Endpunkt bliebe
 * ein in der Luecke beendeter Durchlauf auf „Laeuft" stehen. Abgeglichen wird nur
 * die Live-Liste der schon beobachteten Projekte; welche Projekte angebunden sind,
 * aendert ein Verbindungsabriss nicht.
 *
 * <p>Selbst ausgeloeste und beobachtete Laeufe stehen in einer Liste (NEX-1292): fuer
 * die Sektion sind es dieselben Eintraege aus demselben Endpunkt und demselben Topic,
 * und die Sortierung „neueste zuerst" ordnet sie ohne Ansehen der Herkunft.
 *
 * <p>Gezeigt werden nur laufende Durchlaeufe (NEX-2294), beim Laden, beim Abgleich und bei
 * Meldungen ueber das Topic. Der Endpunkt liefert auch beendete; die gehoeren in die Testlaeufe.
 * Meldet das Topic das Ende eines Laufs, faellt seine Karte heraus.
 */
@Component({
  selector: 'app-live-pipeline-section',
  imports: [
    RouterLink,
    MatIcon,
    TranslatePipe,
    SectionTitleComponent,
    LiveReconnectHintComponent,
    LivePipeCardComponent,
  ],
  providers: [LivePipelineService],
  templateUrl: './live-pipeline-section.component.html',
  styleUrl: './live-pipeline-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LivePipelineSectionComponent {
  private readonly projects = inject(ProjectService);
  private readonly repositories = inject(RepositoryBindingService);
  private readonly feed = inject(LivePipelineService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly headingId = 'live-pipeline-heading';

  /** Leer heisst: alle eigenen Projekte (Projektfilter „Alle“). */
  projectIds = input<string[]>([]);

  private readonly watchedProjectIds = signal<string[]>([]);

  /**
   * Haengt an allen eigenen Projekten, nicht an der Auswahl: hat nur die Auswahl kein
   * Repository, bleibt die Sektion mit ihrer leeren Zeile stehen, statt zu verschwinden.
   */
  private readonly repositoryProjectIds = signal<string[]>([]);

  private feedStarted = false;

  protected readonly pipelines = signal<LivePipeline[]>([]);

  protected readonly hasRepository = computed(() => this.repositoryProjectIds().length > 0);

  /**
   * Die Karten bleiben stehen, wenn die Verbindung faellt: sie zeigen den letzten
   * bekannten Stand, und der Hinweis sagt, dass er nicht mehr fortgeschrieben wird
   * (NEX-1307). Das gilt auch fuer die leere Zeile — „nichts laeuft" ist ohne
   * Verbindung ebenso nur der letzte Stand.
   */
  protected readonly connectionLost = computed(() => this.feed.connection() === 'lost');

  protected readonly key = livePipelineKey;

  /**
   * Der Absprung in die Testlauf-Uebersicht. Die Uebersicht haengt am Projekt, die
   * Sektion nicht — gezeigt wird deshalb das Projekt der obersten Karte, ersatzweise
   * das erste mit angebundenem Repository.
   */
  protected readonly allRunsLink = computed(() => {
    const projectId = this.pipelines()[0]?.projectId ?? this.watchedProjectIds()[0];
    return projectId ? ['/projects', projectId, 'test-runs'] : null;
  });

  constructor() {
    const repositoryProjectIds = this.projects.listForCurrentUser().pipe(
      catchError(() => of([])),
      switchMap((projects) => this.projectIdsWithRepository(projects.map((project) => project.id))),
      tap((projectIds) => this.repositoryProjectIds.set(projectIds)),
    );

    combineLatest([repositoryProjectIds, toObservable(this.projectIds)])
      .pipe(
        map(([withRepository, selected]) =>
          selected.length === 0 ? withRepository : withRepository.filter((id) => selected.includes(id)),
        ),
        distinctUntilChanged<string[]>(sameIds),
        switchMap((projectIds) => this.livePipelines(projectIds).pipe(map((pipelines) => ({ projectIds, pipelines })))),
        takeUntilDestroyed(),
      )
      .subscribe(({ projectIds, pipelines }) => {
        if (this.feedStarted) this.feed.disconnect();
        this.watchedProjectIds.set(projectIds);
        this.pipelines.set(runningLivePipelines(pipelines));
        this.feed.connect(projectIds);
        this.feedStarted = true;
      });

    this.feed.events
      .pipe(takeUntilDestroyed())
      .subscribe(({ projectId, entry }) => this.applyFeedEntry(projectId, entry));

    this.feed.reconnected
      .pipe(
        switchMap(() => this.reloadedLivePipelines(this.watchedProjectIds())),
        takeUntilDestroyed(),
      )
      .subscribe((reloaded) => this.replaceReloaded(reloaded));

    this.destroyRef.onDestroy(() => this.feed.disconnect());
  }

  /**
   * Ein Projekt, dessen Bindungen sich nicht laden lassen, gilt als unangebunden:
   * die Sektion kann fuer dieses Projekt ohnehin nichts zeigen, und ein Fehler darf
   * die uebrigen Projekte nicht mitnehmen.
   */
  private projectIdsWithRepository(projectIds: string[]): Observable<string[]> {
    return forkJoinOrEmpty(
      projectIds.map((projectId) =>
        this.repositories.list(projectId).pipe(
          map((bindings) => (bindings.length > 0 ? projectId : null)),
          catchError(() => of(null)),
        ),
      ),
    ).pipe(map((ids) => ids.filter((id): id is string => id !== null)));
  }

  private livePipelines(projectIds: string[]): Observable<LivePipeline[]> {
    return forkJoinOrEmpty(
      projectIds.map((projectId) =>
        this.projectLivePipelines(projectId).pipe(catchError(() => of([] as LivePipeline[]))),
      ),
    ).pipe(map((lists) => lists.flat()));
  }

  /**
   * Ein Projekt, dessen Abgleich scheitert, fehlt im Ergebnis und behaelt damit seine
   * Karten: der letzte Stand ist hoechstens veraltet, eine leere Liste dagegen falsch.
   */
  private reloadedLivePipelines(projectIds: string[]): Observable<Map<string, LivePipeline[]>> {
    return forkJoinOrEmpty(
      projectIds.map((projectId) =>
        this.projectLivePipelines(projectId).pipe(
          map((pipelines): [string, LivePipeline[]] => [projectId, pipelines]),
          catchError(() => of(null)),
        ),
      ),
    ).pipe(map((results) => new Map(results.filter((result) => result !== null))));
  }

  private projectLivePipelines(projectId: string): Observable<LivePipeline[]> {
    return this.feed.list(projectId).pipe(map((entries) => entries.map((entry) => ({ ...entry, projectId }))));
  }

  private replaceReloaded(reloaded: Map<string, LivePipeline[]>): void {
    this.pipelines.update((pipelines) =>
      runningLivePipelines([
        ...pipelines.filter((pipeline) => !reloaded.has(pipeline.projectId)),
        ...[...reloaded.values()].flat(),
      ]),
    );
  }

  /**
   * Ereignisse fremder Projekte fallen heraus: der Broker stellt nur abonnierte
   * Topics zu, aber die Sektion soll auch dann nichts zeigen, was sie beim Laden
   * nicht gezeigt haette.
   *
   * <p>Ein Eintrag ersetzt den mit derselben Kennung; sein Ende nimmt die Karte heraus.
   */
  private applyFeedEntry(projectId: string, entry: LivePipelineEntry): void {
    if (!this.watchedProjectIds().includes(projectId)) {
      return;
    }
    const updated: LivePipeline = { ...entry, projectId };
    const key = livePipelineKey(updated);
    this.pipelines.update((pipelines) =>
      runningLivePipelines([updated, ...pipelines.filter((pipeline) => livePipelineKey(pipeline) !== key)]),
    );
  }
}

/** Wie {@code forkJoin}, aber ohne Quelle mit einer leeren Liste statt nie. */
function forkJoinOrEmpty<T>(sources: Observable<T>[]): Observable<T[]> {
  return sources.length === 0 ? of([]) : forkJoin(sources);
}
